import { FC, useMemo } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { DeploymentStatusLabel } from "@hostingbv/lib/src/components/DeploymentStatusLabel";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { useCertificates, useServers } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { faAdd, faFileShield, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { Button, IconButton } from "@hostingbv/lib/src/components/form/Button";
import { useApiClient } from "@hostingbv/lib/src/api";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { config } from "../config";
import { FilterableDataTable, navigateRow } from "@ventumsoftware/react-toolkit";

export const Certificates: FC = () => {
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)

  return (
    <ContentContainer>
      <PageIconHeader icon={faFileShield}>
        <Breadcrumbs crumbs={[]} currentPage={"Certificates"} />
        <PageHeader>Certificates</PageHeader>
      </PageIconHeader>
      {can('create', 'certificates') && <AddCertificateForm />}
      <CertificateOverview />
    </ContentContainer>
  );
}

const AddCertificateForm: FC = () => {
  const navigate = useNavigate()
  return <Callout>
    <SectionHeader>Add new certificate</SectionHeader>
    <Paragraph>
      Add new certificates that can be used for tls by sites.
    </Paragraph>
    <Button size={"sm"} type={"primary"} text={"Add new certificate"} icon={faAdd} onClick={() => navigate('/certificates/add')} />
  </Callout>
}

const CertificateOverview: FC = () => {
  const navigate = useNavigate()
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)

  const { data: certificates, isLoading: isLoadingCertificates, fetch: fetchCertificates } = useCertificates()

  const { data: servers, isLoading: isLoadingServers, fetch: fetchServers } = useServers()
  const serverLookupTable = useMemo(
    () => new Map((servers ?? []).map((s) => [s.id, s])),
    [servers]
  )

  const mappedCertificates = useMemo(() => {
    return (certificates ?? [])
      .map((certificate) => ({
        ...certificate,
        server: serverLookupTable.get(certificate.windhostingServer) ?? null,
      }))
  }, [certificates, serverLookupTable])

  const { getRealtimeStatus } = useRealtimeDeployments()!

  return <div className={"my-8"}>
    <SectionHeader>Your Certificates</SectionHeader>
    <FilterableDataTable
      keyProperty={'id'}
      loading={isLoadingCertificates || isLoadingServers}
      data={mappedCertificates ?? []}
      searchableProperties={['displayName']}
      actions={<IconButton icon={faRefresh} onClick={async () => { await Promise.all([fetchCertificates(), fetchServers()]) }} type={"secondary"} size={"sm"} />}
      columns={[
            {
              header: 'Name',
              property: 'displayName',
              transform: (name) => <div className={"flex items-center space-x-2"}>{name}</div>
            },
            {
              header: 'Type & Server',
              property: 'server',
              transform: (server, row) => {
                if (can('view', 'servers')) {
                  return <div>
                    <div className={"text-xs text-slate-500 -mb-1"}>{{ 'acme_certificate': "Acme Certificate" }[row.type]}</div>
                    <div className={"text-sm"}>{server?.serverType === 'windhosting_server' ? server.displayName.substring(0, server.displayName.lastIndexOf(' ')) : server?.displayName.split('.').slice(0, -2).join('.') ?? 'n/a'}</div>
                  </div>
                }
                return <div>{{ 'http-01': "http", 'dns-01': 'dns' }[row.challengeType]}</div>
              },
            },
            {
              header: 'Expires On',
              property: 'validUntil',
              transform: (date) => {
                const diffDays = (+new Date(date) - +new Date()) / (1000 * 60 * 60 * 24);
                const isUrgent = diffDays >= -1 && diffDays <= 30;

                return (
                  <div className={`rounded-sm px-2 tracking-wide ${isUrgent ? 'bg-red-600 text-white' : ''}`}>
                    {new Date(date).toLocaleDateString()}
                  </div>
                );
              }
            },
            {
              header: 'Deployment status',
              property: 'deploymentStatus',
              transform: (status, row) => <DeploymentStatusLabel status={getRealtimeStatus(row.id, status)} />
            },
      ]}
      onClickRow={(row, e) => navigateRow(navigate, `/certificates/${row.id}`, e)}
    />
  </div>
}
